// src/components/Dashboard/StudentDashboard.tsx
//
// In Dashboard.tsx:
//   import StudentDashboard from './StudentDashboard';
//   // Then replace the entire student return (everything after `if (isTeacher) ...`) with:
//   return <StudentDashboard />;
 
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { StoryService, StoryData } from '../../services/storyService';
import { getActiveDailyChallenge, getActiveWeeklyChallenge } from '../Storybook/challengeConfig';
import { RegistrationGate } from '../auth/RegistrationGate';
import JoinClassModal from '../Student/JoinClassModal';
import TodoList from './TodoList';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  primary:            '#6a37d4',
  primaryLight:       '#ae8dff',
  primaryContainer:   '#ede8ff',
  secondary:          '#006947',
  secondaryContainer: '#d1fae5',
  tertiary:           '#6f5900',
  tertiaryContainer:  '#fed01b',
  surface:            '#f3f6ff',
  surfaceLow:         '#eaf1ff',
  surfaceHigh:        '#d6e3f9',
  surfaceHighest:     '#cfdef5',
  surfaceLowest:      '#ffffff',
  onBackground:       '#252f3c',
  onSurface:          '#252f3c',
  onSurfaceVariant:   '#525c6a',
  outlineVariant:     '#a4aebe',
  error:              '#b41340',
  warm:               '#fffbf0',  // warm paper tone for draft cards
};
 
// ── Rarity helpers ────────────────────────────────────────────────────────────
const RARITY_CONFIG: Record<string, { color: string; glow: string; label: string }> = {
  common:   { color: '#9CA3AF', glow: 'rgba(156,163,175,0.25)', label: 'Common'   },
  uncommon: { color: '#10B981', glow: 'rgba(16,185,129,0.25)',  label: 'Uncommon' },
  rare:     { color: '#3B82F6', glow: 'rgba(59,130,246,0.3)',   label: 'Rare'     },
  epic:     { color: '#A855F7', glow: 'rgba(168,85,247,0.3)',   label: 'Epic'     },
  glowing:  { color: '#F59E0B', glow: 'rgba(245,158,11,0.35)',  label: 'Glowing'  },
};
const rarityColor = (r: string) => RARITY_CONFIG[r]?.color ?? C.primary;
const rarityGlow  = (r: string) => RARITY_CONFIG[r]?.glow  ?? 'transparent';
 
// ── Nav item ──────────────────────────────────────────────────────────────────
const NavItem: React.FC<{
  icon: string; label: string; active: boolean;
  onClick: () => void; badge?: string;
}> = ({ icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%', padding: '11px 16px',
      background: active ? C.primaryContainer : 'transparent',
      color: active ? C.primary : C.onSurfaceVariant,
      border: 'none', borderRadius: '14px',
      textAlign: 'left', fontSize: '0.875rem', fontWeight: '700',
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
      fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.15s',
    }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.surfaceHigh; }}
    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
  >
    <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{icon}</span>
    <span style={{ flex: 1 }}>{label}</span>
    {badge && (
      <span style={{
        background: C.tertiaryContainer, color: C.tertiary,
        padding: '2px 8px', borderRadius: '9999px',
        fontSize: '0.65rem', fontWeight: '800',
      }}>
        {badge}
      </span>
    )}
  </button>
);
 
// ── Main component ────────────────────────────────────────────────────────────
const StudentDashboard: React.FC = () => {
  const { xp, level, streak, isChallengeCompleted, isGuestMode, getProgressSummary } = useProgress();
  const navigate = useNavigate();
  const loc      = useLocation();
  const { currentUser } = useAuth();
  const username = currentUser?.displayName || 'Writer';
 
  const [isMobile,            setIsMobile]            = useState(window.innerWidth < 768);
  const [stories,             setStories]             = useState<StoryData[]>([]);
  const [loading,             setLoading]             = useState(true);
  const [showChallengeGate,   setShowChallengeGate]   = useState(false);
  const [showJoinClassModal,  setShowJoinClassModal]  = useState(false);
  const [studentClasses,      setStudentClasses]      = useState<{ id: string; name: string; teacherName: string; joinCode: string }[]>([]);
  const [loadingClasses,      setLoadingClasses]      = useState(false);
  const [challengeTab,        setChallengeTab]        = useState<'daily' | 'weekly'>('daily');
  const hasLoadedRef = useRef(false);
 
  const isActive = (path: string) => loc.pathname === path;
 
  const dailyChallenge  = getActiveDailyChallenge();
  const weeklyChallenge = getActiveWeeklyChallenge();
  const activeChallenge = challengeTab === 'daily' ? dailyChallenge : weeklyChallenge;

  const countWords = (story: StoryData): number => {
    const chapters = (story as any).content?.chapters ?? [];
    return chapters.reduce((total: number, chapter: any) => {
      const hasDirectContent = chapter.content && chapter.content.trim().length > 0;
      const raw = hasDirectContent
        ? chapter.content
        : (chapter.blocks ?? []).map((b: any) => b.content || '').join(' ');
      const words = raw.replace(/<[^>]*>/g, '').trim().split(/\s+/).filter((w: string) => w.length > 0);
      return total + words.length;
    }, 0);
  };
 
  // ── Level / XP helpers ────────────────────────────────────────────────────
  const getXPForLevel   = (l: number) => l <= 1 ? 0 : Math.floor(500 * Math.pow(1.4, l - 2));
  const getTotalXP      = (l: number) => { let t = 0; for (let i = 2; i <= l; i++) t += getXPForLevel(i); return t; };
  const levelProgress   = (() => {
    const cur    = xp - getTotalXP(level);
    const needed = getTotalXP(level + 1) - getTotalXP(level);
    return { current: cur, needed, percent: Math.min(100, Math.max(0, (cur / needed) * 100)) };
  })();
 
  const getLevelTitle = (l: number) => {
    if (l >= 50) return '👑 Grand Master';
    if (l >= 40) return '🧙 Writing Wizard';
    if (l >= 30) return '📖 Epic Author';
    if (l >= 20) return '⭐ Story Star';
    if (l >= 10) return '📚 Chapter Champion';
    if (l >= 5)  return '⚔️ Word Warrior';
    if (l >= 2)  return '🌱 Story Sprout';
    return '📝 Story Starter';
  };
 
  // ── Lifecycle ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
 
  useEffect(() => {
    if (!hasLoadedRef.current && currentUser) {
      hasLoadedRef.current = true;
      StoryService.getUserStories(currentUser.uid)
        .then(all => setStories(all.filter(s => !s.isChallenge)))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [currentUser]);
 
  const loadStudentClasses = useCallback(async () => {
    if (!currentUser) return;
    setLoadingClasses(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'classes'), where(`students.${currentUser.uid}`, '==', true))
      );
      setStudentClasses(snap.docs.map(d => ({
        id:          d.id,
        name:        d.data().name || 'Unnamed Class',
        teacherName: d.data().teacherName || 'Teacher',
        joinCode:    d.data().joinCode || '',
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingClasses(false);
    }
  }, [currentUser]);
 
  useEffect(() => { loadStudentClasses(); }, [loadStudentClasses]);
 
  const startChallenge = (type: 'daily' | 'weekly') => {
    if (isGuestMode) { setShowChallengeGate(true); return; }
    const ch = type === 'daily' ? dailyChallenge : weeklyChallenge;
    navigate('/regular-book-editor', {
      state: {
        isChallenge: true, challengeType: ch.type,
        challengePrompt: ch.prompt, challengeTitle: ch.title,
        challengeXP: ch.xpReward, challengeGoal: { type: 'words', target: ch.wordTarget },
      },
    });
  };
 
  // ── Derived ───────────────────────────────────────────────────────────────
  const drafts         = stories.filter(s => !s.isPublished).slice(0, 4);
  const publishedCount = stories.filter(s => s.isPublished).length;
  const totalWords = stories.reduce((sum, s) => sum + countWords(s), 0);
 
  // Placeholder cards — replace with real card data when available
  const recentCards = [
    { id: 1, name: 'Captain Plot Twist', rarity: 'rare',     icon: '🎭', isNew: true  },
    { id: 2, name: 'Mystery Detective',  rarity: 'uncommon', icon: '🔍', isNew: false },
    { id: 3, name: 'Space Explorer',     rarity: 'common',   icon: '🚀', isNew: false },
  ];
 
  // ── Sidebar ───────────────────────────────────────────────────────────────
  const Sidebar = () => (
    <div style={{
      position: 'fixed', top: 55, left: 0, bottom: 0,
      width: '220px', background: C.surfaceLow,
      borderRadius: '0 24px 24px 0', padding: '24px 12px',
      display: 'flex', flexDirection: 'column', gap: '4px',
      zIndex: 40, overflowY: 'auto',
      boxShadow: '4px 0 16px rgba(0,0,0,0.04)',
    }}>
      <NavItem icon="🏠" label="Dashboard"  active={isActive('/dashboard')}  onClick={() => navigate('/dashboard')} />
      <NavItem icon="📚" label="My Stories" active={isActive('/my-stories')} onClick={() => navigate('/my-stories')} />
      <NavItem icon="✏️" label="Write New"  active={isActive('/write') || isActive('/regular-book-editor')} onClick={() => navigate('/write')} />
      <NavItem icon="🃏" label="My Cards"   active={isActive('/my-cards')}   onClick={() => navigate('/my-cards')} badge="Soon" />
 
      <div style={{ flex: 1 }} />
 
      {/* Level widget */}
      <div style={{ padding: '12px', background: C.primaryContainer, borderRadius: '16px', marginTop: '16px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: C.primary, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px', fontFamily: "'Epilogue', sans-serif" }}>
          Level {level}
        </div>
        <div style={{ fontSize: '0.72rem', color: C.onSurfaceVariant, marginBottom: '8px' }}>
          {getLevelTitle(level)}
        </div>
        <div style={{ height: '5px', background: C.surfaceHigh, borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{
            width: `${levelProgress.percent}%`, height: '100%',
            background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
            borderRadius: '3px', transition: 'width 0.5s',
          }} />
        </div>
        <div style={{ fontSize: '0.68rem', color: C.onSurfaceVariant, marginTop: '4px' }}>
          {levelProgress.current.toLocaleString()} / {levelProgress.needed.toLocaleString()} XP
        </div>
      </div>
    </div>
  );
 
  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {!isMobile && <Sidebar />}
 
      <div style={{
        marginLeft: isMobile ? 0 : '220px',
        padding: isMobile ? '20px 16px 80px' : '40px 44px',
        minHeight: '100vh', boxSizing: 'border-box',
      }}>
 
        {/* ── HEADER ────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '16px', marginBottom: '8px', flexWrap: 'wrap',
        }}>
          <div>
            <h1 style={{
              fontFamily: "'Epilogue', sans-serif",
              fontSize: isMobile ? '1.9rem' : '2.6rem',
              fontWeight: '900', color: C.onBackground, margin: 0, lineHeight: '1.1',
            }}>
              Hey, <span style={{ color: C.primary, fontStyle: 'italic' }}>{username}!</span>
            </h1>
            <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '6px 0 0 0' }}>
              {drafts.length > 0
                ? `You've got ${drafts.length} stor${drafts.length === 1 ? 'y' : 'ies'} waiting.`
                : 'Ready to write something amazing?'}
            </p>
          </div>
 
          {/* Streak badge */}
          {streak > 0 && (
            <div style={{
              background: C.tertiaryContainer,
              border: `2px solid ${C.tertiary}30`,
              borderRadius: '20px', padding: '10px 18px',
              display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: `0 4px 14px ${C.tertiary}20`,
            }}>
              <span style={{ fontSize: '1.5rem' }}>🔥</span>
              <div>
                <div style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.3rem', fontWeight: '900', color: C.tertiary, lineHeight: 1 }}>
                  {streak}
                </div>
                <div style={{ fontSize: '0.65rem', fontWeight: '800', color: C.tertiary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  day streak
                </div>
              </div>
            </div>
          )}
        </div>
 
        {/* XP strip */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: '800', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Level {level} · {getLevelTitle(level)}
            </span>
            <span style={{ fontSize: '0.7rem', color: C.onSurfaceVariant }}>
              {levelProgress.current.toLocaleString()} / {levelProgress.needed.toLocaleString()} XP
            </span>
          </div>
          <div style={{ height: '7px', background: C.surfaceHigh, borderRadius: '4px', overflow: 'hidden', maxWidth: '420px' }}>
            <div style={{
              width: `${levelProgress.percent}%`, height: '100%', borderRadius: '4px',
              background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
              transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                animation: 'shimmer 2.2s infinite',
              }} />
            </div>
          </div>
        </div>
 
        {/* ── GUEST BANNER ─────────────────────────────────────────────── */}
        {isGuestMode && xp > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #FBBF24, #F59E0B)',
            padding: '18px 22px', borderRadius: '20px', marginBottom: '24px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '16px', flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1rem', color: '#1a3a52', marginBottom: '3px' }}>
                🎉 You're on a roll!
              </div>
              <div style={{ fontSize: '0.85rem', color: '#78350F', fontWeight: '600' }}>
                Sign up to save <strong>{getProgressSummary()}</strong> and unlock publishing.
              </div>
            </div>
            <button
              onClick={() => navigate('/signup')}
              style={{ background: '#1a3a52', color: '#fff', border: 'none', borderRadius: '12px', padding: '11px 22px', fontSize: '0.875rem', fontWeight: '800', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Save my progress →
            </button>
          </div>
        )}
 
        {/* ── HERO ROW: drafts + scrib cards ───────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : drafts.length > 0 ? '1fr 260px' : '1fr',
          gap: '16px', marginBottom: '20px', alignItems: 'start',
        }}>
 
          {/* Draft hero */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1rem', color: C.onBackground, margin: 0 }}>
                Keep Writing
              </h2>
              <button onClick={() => navigate('/my-stories')} style={{ background: 'none', border: 'none', color: C.primary, fontWeight: '700', fontSize: '0.825rem', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                All stories →
              </button>
            </div>
 
            {loading ? (
              <div style={{ background: C.surfaceLowest, borderRadius: '20px', height: '160px', border: `1px solid ${C.surfaceHigh}`, opacity: 0.5 }} />
            ) : drafts.length === 0 ? (
              /* Empty state */
              <div
                onClick={() => navigate('/write')}
                style={{
                  background: C.warm,
                  borderRadius: '20px', padding: '32px 28px',
                  border: `2px dashed ${C.outlineVariant}`,
                  display: 'flex', alignItems: 'center', gap: '20px',
                  cursor: 'pointer', transition: 'all 0.15s', flexWrap: 'wrap',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = C.primary; (e.currentTarget as HTMLElement).style.background = C.primaryContainer; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = C.outlineVariant; (e.currentTarget as HTMLElement).style.background = C.warm; }}
              >
                <div style={{ fontSize: '3rem' }}>✏️</div>
                <div>
                  <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.1rem', color: C.onBackground, marginBottom: '4px' }}>
                    Start your first story
                  </div>
                  <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant }}>Every bestseller starts with a blank page.</div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Featured draft (first) */}
                <div
                  onClick={() => navigate('/regular-book-editor', { state: { storyId: drafts[0].id } })}
                  style={{
                    background: C.warm,
                    borderRadius: '20px', padding: '0',
                    border: `1px solid ${C.surfaceHigh}`,
                    display: 'flex', overflow: 'hidden',
                    cursor: 'pointer', minHeight: '120px',
                    boxShadow: '0 4px 16px rgba(106,55,212,0.08)',
                    transition: 'box-shadow 0.15s, transform 0.15s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 10px 32px rgba(106,55,212,0.15)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(106,55,212,0.08)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                >
                  {/* Book spine accent */}
                  <div style={{
                    width: '10px', flexShrink: 0,
                    background: `linear-gradient(180deg, ${C.primary}, ${C.primaryLight})`,
                  }} />
                  <div style={{ padding: '22px 22px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                      {drafts[0].tags?.[0] && (
                        <span style={{ background: C.primaryContainer, color: C.primary, padding: '2px 10px', borderRadius: '9999px', fontSize: '0.62rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-block', marginBottom: '8px' }}>
                          {drafts[0].tags[0]}
                        </span>
                      )}
                      <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.3rem', color: C.onBackground, margin: 0, lineHeight: '1.2' }}>
                        {drafts[0].title}
                      </h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.78rem', color: C.onSurfaceVariant, fontWeight: '600' }}>
                      {countWords(drafts[0]).toLocaleString()} words written
                      </span>
                      <div style={{
                        background: C.primary, color: '#fff',
                        borderRadius: '10px', padding: '7px 16px',
                        fontSize: '0.78rem', fontWeight: '700',
                        fontFamily: "'Epilogue', sans-serif",
                      }}>
                        Continue ✏️
                      </div>
                    </div>
                  </div>
                </div>
 
                {/* Secondary drafts (smaller row) */}
                {drafts.length > 1 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.min(drafts.length - 1, 3)}, 1fr)`,
                    gap: '10px',
                  }}>
                    {drafts.slice(1, 4).map(story => (
                      <div
                        key={story.id}
                        onClick={() => navigate('/regular-book-editor', { state: { storyId: story.id } })}
                        style={{
                          background: C.surfaceLowest, borderRadius: '14px', padding: '0',
                          border: `1px solid ${C.surfaceHigh}`, overflow: 'hidden',
                          display: 'flex', cursor: 'pointer', minHeight: '76px',
                          transition: 'box-shadow 0.15s, transform 0.15s',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 14px rgba(0,0,0,0.1)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                      >
                        <div style={{ width: '6px', flexShrink: 0, background: C.surfaceHigh }} />
                        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
                          <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.82rem', color: C.onBackground, lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>
                            {story.title}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: C.onSurfaceVariant }}>
                            {countWords(story).toLocaleString()} words
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
 
            {/* Start new story button */}
            <button
              onClick={() => navigate('/write')}
              style={{
                marginTop: '10px', width: '100%',
                background: 'transparent',
                border: `1.5px solid ${C.outlineVariant}`,
                borderRadius: '12px', padding: '10px',
                fontSize: '0.82rem', fontWeight: '700', color: C.onSurfaceVariant,
                cursor: 'pointer', fontFamily: "'Epilogue', sans-serif",
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; e.currentTarget.style.background = C.primaryContainer; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; e.currentTarget.style.background = 'transparent'; }}
            >
              ✨ Start something new
            </button>
          </div>
 
          {/* Scrib cards panel */}
          {!isMobile && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1rem', color: C.onBackground, margin: 0 }}>
                  🃏 Your Cards
                </h2>
                <button onClick={() => navigate('/my-cards')} style={{ background: 'none', border: 'none', color: C.primary, fontWeight: '700', fontSize: '0.825rem', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  View all →
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {recentCards.map(card => {
                  const rc = RARITY_CONFIG[card.rarity] ?? RARITY_CONFIG.common;
                  return (
                    <div
                      key={card.id}
                      style={{
                        background: C.surfaceLowest, borderRadius: '14px', padding: '12px 14px',
                        border: `1.5px solid ${rc.color}30`,
                        display: 'flex', alignItems: 'center', gap: '12px',
                        boxShadow: `0 2px 10px ${rc.glow}`,
                        position: 'relative', overflow: 'hidden',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        cursor: 'default',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 6px 20px ${rc.glow}`; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 2px 10px ${rc.glow}`; }}
                    >
                      {/* Rarity color bar */}
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: rc.color, borderRadius: '14px 0 0 14px' }} />
                      <div style={{ width: 36, height: 36, borderRadius: '10px', background: `${rc.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                        {card.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: '0.82rem', color: C.onBackground, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {card.name}
                        </div>
                        <div style={{ fontSize: '0.65rem', fontWeight: '800', color: rc.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {rc.label}
                        </div>
                      </div>
                      {card.isNew && (
                        <span style={{ background: C.tertiaryContainer, color: C.tertiary, padding: '2px 7px', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: '800', flexShrink: 0 }}>
                          NEW
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: '8px', padding: '10px 12px', background: C.primaryContainer, borderRadius: '12px', fontSize: '0.75rem', color: C.primary, fontWeight: '600', textAlign: 'center' }}>
                🎁 Write to earn new cards!
              </div>
            </div>
          )}
        </div>
 
        {/* ── CHALLENGE CARD ────────────────────────────────────────────── */}
        <div style={{ marginBottom: '24px' }}>
          {/* Tab toggle */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
            {(['daily', 'weekly'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setChallengeTab(tab)}
                style={{
                  padding: '5px 14px',
                  background: challengeTab === tab ? C.onBackground : 'transparent',
                  color:      challengeTab === tab ? '#fff' : C.onSurfaceVariant,
                  border:     `1.5px solid ${challengeTab === tab ? C.onBackground : C.outlineVariant}`,
                  borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700',
                  cursor: 'pointer', fontFamily: "'Epilogue', sans-serif",
                  transition: 'all 0.15s',
                }}
              >
                {tab === 'daily' ? '📅 Daily' : '🏆 Weekly'}
              </button>
            ))}
            {isChallengeCompleted(challengeTab) && (
              <span style={{ padding: '5px 12px', background: C.secondaryContainer, color: C.secondary, borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                ✅ Done for today!
              </span>
            )}
          </div>
 
          {/* The card itself */}
          <div
            style={{
              background: C.onBackground,
              borderRadius: '20px', padding: '24px 28px',
              display: 'flex', alignItems: 'center', gap: '20px',
              position: 'relative', overflow: 'hidden',
              flexWrap: 'wrap',
            }}
          >
            {/* Decorative bg circle */}
            <div style={{ position: 'absolute', right: '-40px', top: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
            <div style={{ position: 'absolute', right: '40px', bottom: '-60px', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
 
            {/* Icon */}
            <div style={{ width: 52, height: 52, borderRadius: '16px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', flexShrink: 0 }}>
              {activeChallenge.icon}
            </div>
 
            {/* Content */}
            <div style={{ flex: 1, minWidth: '180px', position: 'relative', zIndex: 1 }}>
              <div style={{ fontSize: '0.65rem', fontWeight: '800', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '5px' }}>
                {challengeTab === 'daily' ? "Today's Prompt" : "This Week's Challenge"}
              </div>
              <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: '#fff', lineHeight: '1.35', marginBottom: '6px' }}>
                {activeChallenge.title}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', lineHeight: '1.4' }}>
                {activeChallenge.description}
              </div>
            </div>
 
            {/* XP + CTA */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0, position: 'relative', zIndex: 1 }}>
              <div style={{ background: C.tertiaryContainer, color: C.tertiary, padding: '4px 12px', borderRadius: '9999px', fontSize: '0.78rem', fontWeight: '800' }}>
                +{activeChallenge.xpReward} XP ✨
              </div>
              <button
                onClick={() => { if (!isChallengeCompleted(challengeTab)) startChallenge(challengeTab); }}
                disabled={isChallengeCompleted(challengeTab)}
                style={{
                  background: isChallengeCompleted(challengeTab) ? 'rgba(255,255,255,0.1)' : '#fff',
                  color:      isChallengeCompleted(challengeTab) ? 'rgba(255,255,255,0.5)' : C.primary,
                  border: 'none', borderRadius: '9999px', padding: '10px 20px',
                  fontSize: '0.82rem', fontWeight: '800',
                  cursor: isChallengeCompleted(challengeTab) ? 'not-allowed' : 'pointer',
                  fontFamily: "'Epilogue', sans-serif", whiteSpace: 'nowrap',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => { if (!isChallengeCompleted(challengeTab)) e.currentTarget.style.opacity = '0.88'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                {isChallengeCompleted(challengeTab) ? '✅ Completed' : 'Start Writing →'}
              </button>
            </div>
          </div>
        </div>
 
        {/* ── MY CLASSES ────────────────────────────────────────────────── */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1rem', color: C.onBackground, margin: 0 }}>
              My Classes
            </h2>
            <button
              onClick={() => setShowJoinClassModal(true)}
              style={{ background: 'none', border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', padding: '5px 14px', fontSize: '0.75rem', fontWeight: '700', color: C.onSurfaceVariant, cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
            >
              ➕ Join a class
            </button>
          </div>
 
          {loadingClasses ? (
            <div style={{ height: '72px', background: C.surfaceLowest, borderRadius: '16px', border: `1px solid ${C.surfaceHigh}`, opacity: 0.5 }} />
          ) : studentClasses.length === 0 ? (
            <div
              onClick={() => setShowJoinClassModal(true)}
              style={{ background: C.surfaceLowest, borderRadius: '16px', padding: '20px 22px', border: `2px dashed ${C.outlineVariant}`, display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = C.primary; (e.currentTarget as HTMLElement).style.background = C.primaryContainer; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = C.outlineVariant; (e.currentTarget as HTMLElement).style.background = C.surfaceLowest; }}
            >
              <span style={{ fontSize: '1.5rem' }}>📚</span>
              <div>
                <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.9rem', color: C.onBackground }}>
                  Got a class code from your teacher?
                </div>
                <div style={{ fontSize: '0.8rem', color: C.onSurfaceVariant, marginTop: '2px' }}>
                  Tap to join a class and share your stories.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(studentClasses.length + 1, 3)}, 1fr)`, gap: '10px' }}>
              {studentClasses.map((cls, i) => (
                <div
                  key={cls.id}
                  style={{
                    background: i === 0 ? `linear-gradient(135deg, #1a1060, ${C.primary})` : C.surfaceLowest,
                    borderRadius: '16px', padding: '16px 18px',
                    border: i !== 0 ? `1px solid ${C.surfaceHigh}` : 'none',
                    boxShadow: i === 0 ? `0 4px 16px ${C.primary}30` : '0 1px 4px rgba(0,0,0,0.04)',
                    display: 'flex', flexDirection: 'column', gap: '10px',
                    transition: 'transform 0.15s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                >
                  <div>
                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '0.95rem', color: i === 0 ? '#fff' : C.onBackground }}>
                      {cls.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: i === 0 ? 'rgba(255,255,255,0.7)' : C.onSurfaceVariant, marginTop: '2px' }}>
                      👩‍🏫 {cls.teacherName}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/my-stories?classId=${cls.id}`)}
                    style={{ background: i === 0 ? 'rgba(255,255,255,0.9)' : C.primary, color: i === 0 ? C.primary : '#fff', border: 'none', borderRadius: '9999px', padding: '7px 0', fontSize: '0.775rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", width: '100%' }}
                  >
                    View my work →
                  </button>
                </div>
              ))}
              <div
                onClick={() => setShowJoinClassModal(true)}
                style={{ background: 'transparent', border: `1.5px dashed ${C.outlineVariant}`, borderRadius: '16px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.15s', minHeight: '80px' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = C.surfaceLow; (e.currentTarget as HTMLElement).style.borderColor = C.primary; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.borderColor = C.outlineVariant; }}
              >
                <span>➕</span>
                <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '700', fontSize: '0.78rem', color: C.onSurfaceVariant }}>Join another</span>
              </div>
            </div>
          )}
        </div>
 
        {/* ── TODO LIST ─────────────────────────────────────────────────── */}
        <TodoList isMobile={isMobile} />
 
        {/* ── STATS STRIP (bottom, positive framing) ───────────────────── */}
        <div style={{
          marginTop: '24px',
          background: C.surfaceLowest, borderRadius: '16px',
          padding: '18px 22px', border: `1px solid ${C.surfaceHigh}`,
          display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '0.85rem', color: C.onBackground }}>
            Your story so far
          </div>
          {[
            { value: totalWords.toLocaleString(),     label: 'words written',    color: C.primary   },
            { value: `${publishedCount} published`,   label: 'stories shared',   color: C.secondary },
            { value: `Level ${level}`,                label: getLevelTitle(level), color: C.tertiary },
          ].map(({ value, label, color }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.1rem', color }}>
                {value}
              </span>
              <span style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, fontWeight: '600' }}>
                {label}
              </span>
            </div>
          ))}
        </div>
 
      </div>
 
      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {showChallengeGate && (
        <RegistrationGate
          type="publish" draftTitle="Writing Challenge"
          onRegister={() => navigate('/signup')}
          onCancel={() => setShowChallengeGate(false)}
        />
      )}
      {showJoinClassModal && (
        <JoinClassModal
          onClose={() => setShowJoinClassModal(false)}
          onClassJoined={() => { setShowJoinClassModal(false); loadStudentClasses(); }}
        />
      )}
 
      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};
 
export default StudentDashboard;